import { styled } from "goober";
import { pluralize } from "../lib/inflection";
import { Icon } from "./Icon";
import { PreviewListBox, PreviewSectionLabel } from "./PreviewList";
import { type PrincipalPreview, PrincipalRow } from "./PrincipalPreviewList";
import { Text } from "./Text";
import { type WorkflowPreview, WorkflowRow } from "./WorkflowPreviewList";

export type DisconnectImpact = {
  workflows: WorkflowPreview[];
  gitSpaceNames: string[];
  affectedUserIds?: string[];
};

const ChatRollup = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
`;

export function Count({ n, noun }: { n: number; noun: string }) {
  return (
    <Text variant="text-regular-md" color="var(--text-primary)">
      {n} {pluralize(noun, n)}
    </Text>
  );
}

export function RemovalDescription({
  target,
  principals,
  workflows,
}: {
  target: string;
  principals: readonly PrincipalPreview[];
  workflows: number;
}) {
  const groups = principals.filter((p) => p.kind === "group").length;
  const members = principals.filter((p) => p.kind === "user").length;
  const access =
    groups > 0 && members > 0 ? (
      <>
        <Count n={groups} noun="group" /> and{" "}
        <Count n={members} noun="member" />
      </>
    ) : groups > 0 ? (
      <Count n={groups} noun="group" />
    ) : (
      <Count n={members} noun="member" />
    );
  return (
    <>
      Removing access to {target} affects {access}
      {workflows > 0 ? (
        <>
          , and <Count n={workflows} noun="workflow" /> will need a connector
          replaced.
        </>
      ) : (
        "."
      )}
    </>
  );
}

export function DisconnectWarning({
  principals = [],
  workflows,
  gitSpaceNames,
  chatCount = 0,
}: DisconnectImpact & {
  principals?: readonly PrincipalPreview[];
  chatCount?: number;
}) {
  if (
    principals.length === 0 &&
    workflows.length === 0 &&
    gitSpaceNames.length === 0 &&
    chatCount === 0
  )
    return null;
  const sectionCount =
    (principals.length > 0 ? 1 : 0) +
    (workflows.length > 0 ? 1 : 0) +
    (chatCount > 0 ? 1 : 0);
  const showHeaders = sectionCount > 1;
  return (
    <>
      {(principals.length > 0 || workflows.length > 0 || chatCount > 0) && (
        <PreviewListBox>
          {principals.length > 0 && (
            <>
              {showHeaders && (
                <PreviewSectionLabel>Groups and members</PreviewSectionLabel>
              )}
              {principals.map((p) => (
                <PrincipalRow key={`${p.kind}-${p.id}`} principal={p} />
              ))}
            </>
          )}
          {workflows.length > 0 && (
            <>
              {showHeaders && (
                <PreviewSectionLabel>Affected workflows</PreviewSectionLabel>
              )}
              {workflows.map((w) => (
                <WorkflowRow key={w.id} workflow={w} />
              ))}
            </>
          )}
          {chatCount > 0 && (
            <>
              {showHeaders && (
                <PreviewSectionLabel>Personal chats</PreviewSectionLabel>
              )}
              <ChatRollup>
                <Icon name="ask" size={16} color="var(--text-primary)" />
                <Text variant="text-regular" color="var(--text-primary)">
                  {chatCount} personal {pluralize("chat", chatCount)}
                </Text>
              </ChatRollup>
            </>
          )}
        </PreviewListBox>
      )}
      {gitSpaceNames.length > 0 && (
        <Text variant="text-regular" color="var(--text-secondary)">
          Git sync in {gitSpaceNames.join(", ")} will{" "}
          {workflows.length > 0 ? "also " : ""}lose access.
        </Text>
      )}
    </>
  );
}
